import OtaEmailNotice, { useEmailLinkAcknowledgement } from "./OtaEmailNotice";
import React, { useMemo } from "react";
import {
  Box,
  Button,
  CheckBox,
  Heading,
  Select,
  FormField,
  Text,
  Tip,
  RadioButtonGroup,
} from "grommet";
import { CircleInformation } from "grommet-icons";
import LocalizedEmailEditor from "./LocalizedEmailEditor";
import LocalizedTextEditor, { SMS_MAX_LENGTH } from "./LocalizedTextEditor";
import { useTranslation } from "react-i18next";
import { supportsOfflineFallback } from "../../../common/offerCapabilities";

const WALKINTIME_OPTIONS = [
  { label: "6:00 AM", value: "06:00" },
  { label: "7:00 AM", value: "07:00" },
  { label: "8:00 AM", value: "08:00" },
  { label: "9:00 AM", value: "09:00" },
  { label: "10:00 AM", value: "10:00" },
  { label: "11:00 AM", value: "11:00" },
  { label: "12:00 PM", value: "12:00" },
  { label: "1:00 PM", value: "13:00" },
  { label: "2:00 PM", value: "14:00" },
  { label: "3:00 PM", value: "15:00" },
  { label: "4:00 PM", value: "16:00" },
  { label: "5:00 PM", value: "17:00" },
  { label: "6:00 PM", value: "18:00" },
  { label: "7:00 PM", value: "19:00" },
  { label: "8:00 PM", value: "20:00" },
  { label: "9:00 PM", value: "21:00" },
  { label: "10:00 PM", value: "22:00" },
  { label: "11:00 PM", value: "23:00" },
];

export default function CheckinFlowAccess({
  formValues,
  onChange,
  communications,
  onCommunicationsChange,
  onSaveCommunications,
  savedCommunications,
  isOnlineCheckinEnabled,
  hotel,
  defaults,
}) {
  const { t } = useTranslation();
  const linkAcknowledgement = useEmailLinkAcknowledgement(
    communications?.accessEmail?.body,
  );

  const failActionOptions = useMemo(
    () => [
      { label: t("checkinFlow.access.failNotify"), value: "false" },
      { label: t("checkinFlow.access.failOffline"), value: "true" },
    ],
    [t],
  );

  const isTtlock = hotel?.lockType === "ttlock";
  const isOracode = hotel?.lockType === "oracode";
  const usePasscodes = hotel?.usePasscodes !== false;

  // Determine the correct email template for access instructions
  const accessEmailTemplate = isOracode
    ? "your-oracode"
    : hotel?.sendMobileKeyQuietly && usePasscodes
      ? "your-access-code"
      : "your-keys";

  const handleToggle = (name) => (e) => {
    onChange({ ...formValues, [name]: e.target.checked });
  };

  const handleValueChange = (name, value) => {
    onChange({ ...formValues, [name]: value });
  };

  return (
    <Box id="section-access" gap="medium">
      <Heading level={3} margin="none">
        {t("checkinFlow.access.title")}
      </Heading>
      <Text size="small" color="dark-4">
        {t("checkinFlow.access.description")}
      </Text>

      {/* Send keys at walkin time */}
      <Box gap="small">
        <Heading level={4} margin="none">
          {t("checkinFlow.access.sendKeysWhen")}
        </Heading>

        <Box gap="small">
          <Box direction="row" gap="small" align="center" wrap>
            <CheckBox
              checked={!!formValues.autoSendWalkinTime}
              onChange={(e) => {
                if (e.target.checked) {
                  handleValueChange(
                    "autoSendWalkinTime",
                    formValues.autoSendWalkinTime || "08:00",
                  );
                } else {
                  handleValueChange("autoSendWalkinTime", "");
                }
              }}
              label={t("checkinFlow.access.atTime")}
              toggle
            />
            <FormField margin="none">
              <Select
                size="small"
                value={formValues.autoSendWalkinTime || "08:00"}
                options={WALKINTIME_OPTIONS}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                onChange={({ value }) =>
                  handleValueChange("autoSendWalkinTime", value)
                }
                disabled={!formValues.autoSendWalkinTime}
              />
            </FormField>
            <Tip
              content={`At ${formValues.autoSendWalkinTime || "8:00 AM"}, the keys will be sent every morning for the coming guests. The key will be valid at ${hotel?.checkinTime || "3:00 PM"}. If a guest reserves after ${formValues.autoSendWalkinTime || "8:00 AM"}, the key will be sent before ${hotel?.checkinTime || "3:00 PM"}. If a guest reserves after ${hotel?.checkinTime || "3:00 PM"}, the key will be sent immediately.`}
            >
              <CircleInformation size="small" />
            </Tip>
          </Box>

          {formValues.autoSendWalkinTime && (
            <Box pad={{ left: "medium" }} gap="small">
              {isOnlineCheckinEnabled && (
                <Box direction="row" gap="small" align="center">
                  <CheckBox
                    checked={!!formValues.sendKeysOnlyIfApproved}
                    onChange={handleToggle("sendKeysOnlyIfApproved")}
                    label={t("checkinFlow.access.onlyIfApproved")}
                    toggle
                  />
                  <Tip content={t("checkinFlow.access.onlyIfApprovedTip")}>
                    <CircleInformation size="small" />
                  </Tip>
                </Box>
              )}
              {!isOnlineCheckinEnabled && hotel?.webrezproAgreement && (
                <Box direction="row" gap="small" align="center">
                  <CheckBox
                    checked={!!formValues.onlyIfWRPAgreementCompletedWalkin}
                    onChange={handleToggle("onlyIfWRPAgreementCompletedWalkin")}
                    label={t("checkinFlow.access.onlyIfWrp")}
                    toggle
                  />
                  <Tip content={t("checkinFlow.access.onlyIfWrpTip")}>
                    <CircleInformation size="small" />
                  </Tip>
                </Box>
              )}

              {supportsOfflineFallback(hotel) && (
                <FormField
                  label={t("checkinFlow.access.ifFails")}
                  width="medium"
                >
                  <Select
                    size="medium"
                    labelKey="label"
                    valueKey={{ key: "value", reduce: true }}
                    value={
                      formValues.useFallbackOfflineCodes ? "true" : "false"
                    }
                    options={failActionOptions}
                    onChange={({ value }) =>
                      handleValueChange(
                        "useFallbackOfflineCodes",
                        value === "true",
                      )
                    }
                  />
                </FormField>
              )}
            </Box>
          )}
        </Box>

        <Box gap="small" margin={{ top: "small" }}>
          <CheckBox
            checked={!!formValues.autoSendEnabled}
            onChange={handleToggle("autoSendEnabled")}
            label={t("checkinFlow.access.walkinPms")}
            toggle
          />
          {formValues.autoSendEnabled && !isOracode && (
            <Box pad={{ left: "medium" }}>
              <Box direction="row" gap="small" align="center">
                <CheckBox
                  checked={!!formValues.autoSendValidNow}
                  onChange={handleToggle("autoSendValidNow")}
                  label={t("checkinFlow.access.validImmediately")}
                  toggle
                />
                <Tip content={t("checkinFlow.access.validImmediatelyTip")}>
                  <CircleInformation size="small" />
                </Tip>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Access code type - only for ttlock with passcodes */}
      {isTtlock && usePasscodes && (
        <Box gap="small">
          <Heading level={4} margin="none">
            {t("checkinFlow.access.accessCode")}
          </Heading>
          <RadioButtonGroup
            name="useRandomCode"
            options={[
              { label: t("checkinFlow.access.lastDigits"), value: false },
              { label: t("checkinFlow.access.randomCode"), value: true },
            ]}
            value={!!formValues.useRandomCode}
            onChange={(e) =>
              handleValueChange("useRandomCode", e.target.value === "true")
            }
          />
        </Box>
      )}

      {/* Email */}
      <Box margin={{ top: "medium" }} gap="small">
        <Heading level={4} margin="none">
          {t("checkinFlow.common.email")}
        </Heading>
        {hotel?.disableAllCommunications ? (
          <Text size="small" color="status-critical">
            {t("checkinFlow.common.commsDisabled")}
          </Text>
        ) : (
          <>
            <LocalizedEmailEditor
              value={communications?.accessEmail || defaults?.accessEmail}
              onChange={(val) =>
                onCommunicationsChange({ ...communications, accessEmail: val })
              }
              defaultValue={defaults?.accessEmail}
              useCustom={!!communications?.useCustomAccessEmail}
              onUseCustomChange={(v) => {
                const updated = { ...communications, useCustomAccessEmail: v };
                onSaveCommunications(updated);
              }}
              template={accessEmailTemplate}
              upsells={communications?.accessEmailUpsells || []}
              currency={hotel?.currency}
              onUpsellsChange={(upsells, email) => {
                const updated = {
                  ...communications,
                  accessEmail: email,
                  accessEmailUpsells: upsells,
                };
                onSaveCommunications(updated);
              }}
              tagHint={t("checkinFlow.common.hintTags")}
              tags={[
                "firstName",
                "lastName",
                "hotel",
                "hotelPhone",
                "checkinDate",
                "checkinTime",
                "checkoutDate",
                "checkoutTime",
                "magicLink",
                "RoomsAndCodes",
                ...(accessEmailTemplate !== "your-oracode"
                  ? ["downloadAppButton"]
                  : []),
                ...(accessEmailTemplate === "your-keys"
                  ? ["mobileAppGuide"]
                  : []),
                "upsells",
              ]}
            />
            {!!communications?.useCustomAccessEmail && (
              <>
                <Box gap="xsmall">
                  {!(
                    communications?.accessEmail?.body?.en ??
                    defaults?.accessEmail?.body?.en
                  )
                    ?.replace(/<[^>]*>/g, "")
                    ?.trim() && (
                    <Text size="small" color="status-critical">
                      {t("checkinFlow.common.emailMissing")}
                    </Text>
                  )}
                  {hotel?.language === "fr" &&
                    !(
                      communications?.accessEmail?.body?.fr ??
                      defaults?.accessEmail?.body?.fr
                    )
                      ?.replace(/<[^>]*>/g, "")
                      ?.trim() && (
                      <Text size="small" color="status-warning">
                        {t("checkinFlow.common.frEmailMissing")}
                      </Text>
                    )}
                </Box>
                <Box align="end" gap="xsmall">
                  <OtaEmailNotice
                    hotel={hotel}
                    acknowledgement={linkAcknowledgement}
                  />
                  <Text size="xsmall" color="dark-4">
                    {t("checkinFlow.common.pressEmailSave")}
                  </Text>
                  <Button
                    label={t("checkinFlow.common.save")}
                    primary
                    size="small"
                    disabled={
                      linkAcknowledgement.blocked ||
                      JSON.stringify(communications?.accessEmail) ===
                        JSON.stringify(savedCommunications?.accessEmail)
                    }
                    onClick={() => onSaveCommunications()}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {/* SMS */}
      <Box margin={{ top: "medium" }} gap="small">
        <Heading level={4} margin="none">
          {t("checkinFlow.common.textMessage")}
        </Heading>
        {hotel?.disableAllCommunications ? (
          <Text size="small" color="status-critical">
            {t("checkinFlow.common.commsDisabled")}
          </Text>
        ) : (
          <>
            <LocalizedTextEditor
              value={communications?.accessText || defaults?.accessText}
              onChange={(val) =>
                onCommunicationsChange({ ...communications, accessText: val })
              }
              defaultValue={defaults?.accessText}
              useCustom={!!communications?.useCustomAccessText}
              onUseCustomChange={(v) => {
                const updated = { ...communications, useCustomAccessText: v };
                onSaveCommunications(updated);
              }}
              maxLength={SMS_MAX_LENGTH}
              hint={t("checkinFlow.common.hintTagsWithCodes")}
              tags={[
                "firstName",
                "lastName",
                "hotel",
                "checkinDate",
                "checkinTime",
                "checkoutDate",
                "checkoutTime",
                "roomsAndCodes",
                "mobileAppLink",
              ]}
            />
            {!!communications?.useCustomAccessText && (
              <>
                <Box gap="xsmall">
                  {!(
                    communications?.accessText?.en ?? defaults?.accessText?.en
                  )?.trim() && (
                    <Text size="small" color="status-critical">
                      {t("checkinFlow.common.textMissing")}
                    </Text>
                  )}
                  {hotel?.language === "fr" &&
                    !(
                      communications?.accessText?.fr ?? defaults?.accessText?.fr
                    )?.trim() && (
                      <Text size="small" color="status-warning">
                        {t("checkinFlow.common.frTextMissing")}
                      </Text>
                    )}
                </Box>
                <Box align="end" gap="xsmall">
                  <Text size="xsmall" color="dark-4">
                    {t("checkinFlow.common.pressTextSave")}
                  </Text>
                  <Button
                    label={t("checkinFlow.common.save")}
                    primary
                    size="small"
                    disabled={
                      JSON.stringify(communications?.accessText) ===
                        JSON.stringify(savedCommunications?.accessText) ||
                      (communications?.accessText?.en || "").length >
                        SMS_MAX_LENGTH ||
                      (communications?.accessText?.fr || "").length >
                        SMS_MAX_LENGTH
                    }
                    onClick={() => onSaveCommunications()}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
